import { useEffect, useRef, useState } from "react";
import {
  FaArrowUp,
  FaBell,
  FaChevronLeft,
  FaFacebook,
  FaInstagram,
  FaPhone,
  FaTiktok,
  FaTimes,
  FaWhatsapp,
  FaYoutube,
} from "react-icons/fa";

export const FloatingContact = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNotification, setShowNotification] = useState(true);
  const [pulseAnimation, setPulseAnimation] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // ✨ الحالة الجديدة: هل القائمة مفتوحة؟
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const containerRef = useRef(null);
  const whatsappBtnRef = useRef(null);
  const callBtnRef = useRef(null);
  const backToTopRef = useRef(null);
  const formRef = useRef(null);
  const toggleBtnRef = useRef(null);

  const phoneNumber = "+34614909102";
  const whatsappNumber = "34614909102";
  const whatsappMessage = "Hello! I'm interested in your services.";

  // Check screen size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Show toggle button with delay
  useEffect(() => {
    const timer = setTimeout(() => setButtonsVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Handle scroll for back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle form visibility
  useEffect(() => {
    if (showForm) {
      setFormVisible(true);
    } else {
      const timer = setTimeout(() => setFormVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [showForm]);

  // ✨ إغلاق القائمة عند الضغط خارجها
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isMenuOpen &&
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  // ✨ إغلاق القائمة بمفتاح Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleWhatsAppClick = () => {
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(url, "_blank");

    if (whatsappBtnRef.current) {
      whatsappBtnRef.current.classList.add("click-animation");
      setTimeout(() => {
        whatsappBtnRef.current?.classList.remove("click-animation");
      }, 300);
    }

    setShowNotification(false);
  };

  const handleCallClick = () => {
    window.location.href = `tel:${phoneNumber}`;
    if (callBtnRef.current) {
      callBtnRef.current.classList.add("click-animation");
      setTimeout(() => {
        callBtnRef.current?.classList.remove("click-animation");
      }, 300);
    }
  };

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (backToTopRef.current) {
      backToTopRef.current.classList.add("click-animation");
      setTimeout(() => {
        backToTopRef.current?.classList.remove("click-animation");
      }, 300);
    }
  };

  // Pulse animation toggle
  useEffect(() => {
    const pulseInterval = setInterval(() => {
      setPulseAnimation((prev) => !prev);
    }, 4000);

    const notificationTimeout = setTimeout(() => {
      setShowNotification(false);
    }, 30000);

    return () => {
      clearInterval(pulseInterval);
      clearTimeout(notificationTimeout);
    };
  }, []);

  // ✨ toggle للقائمة
  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
    if (toggleBtnRef.current) {
      toggleBtnRef.current.classList.add("click-animation");
      setTimeout(() => {
        toggleBtnRef.current?.classList.remove("click-animation");
      }, 300);
    }
  };

  return (
    <>
      <div
        className={`floating-contact-container ${isMobile ? "mobile-view" : ""} ${isMenuOpen ? "menu-open" : ""}`}
        ref={containerRef}
      >
        {/* ✨ زر التوجل الرئيسي */}
        <button
          ref={toggleBtnRef}
          className={`floating-btn toggle-btn  ${buttonsVisible ? "visible" : ""} ${isMenuOpen ? "open" : ""} ${pulseAnimation && !isMenuOpen ? "pulse" : ""}`}
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Close menu" : "Open contact menu"}
          aria-expanded={isMenuOpen}
        >
          <span className="toggle-icon-wrap">
            {isMenuOpen ? <FaTimes /> : <FaChevronLeft />}
          </span>
          {showNotification && !isMenuOpen && (
            <div className="notification-badge">
              <FaBell />
            </div>
          )}
          <span className="floating-tooltip">
            {isMenuOpen ? "Close" : "Contact us"}
          </span>
        </button>

        {/* ✨ القايمة نفسها — تظهر لما isMenuOpen = true */}
        <div className={`menu-items ${isMenuOpen ? "visible" : ""}`}>
          {/* Back to Top */}
          {showBackToTop && (
            <button
              ref={backToTopRef}
              className="floating-btn back-to-top-btn"
              onClick={handleBackToTop}
              aria-label="Back to top"
              style={{ "--delay": "0.05s" }}
            >
              <FaArrowUp />
              <span className="floating-tooltip">Back to top</span>
            </button>
          )}

          {/* Instagram */}
          <a
            href="https://www.instagram.com/flyvia.services/"
            target="_blank"
            rel="noopener noreferrer"
            className="floating-btn instagram-btn"
            style={{ "--delay": "0.1s" }}
          >
            <FaInstagram />
            <span className="floating-tooltip">Instagram</span>
          </a>

          {/* Facebook */}
          <a
            href="https://www.facebook.com/profile.php?id=61588256249191"
            target="_blank"
            rel="noopener noreferrer"
            className="floating-btn facebook-btn"
            style={{ "--delay": "0.15s" }}
          >
            <FaFacebook />
            <span className="floating-tooltip">Facebook</span>
          </a>

          {/* TikTok */}
          <a
            href="https://www.tiktok.com/@flyvia.services"
            target="_blank"
            rel="noopener noreferrer"
            className="floating-btn tiktok-btn"
            style={{ "--delay": "0.2s" }}
          >
            <FaTiktok />
            <span className="floating-tooltip">TikTok</span>
          </a>

          {/* YouTube */}
          <a
            href="https://www.youtube.com/@Flyvia-x8d/videos"
            target="_blank"
            rel="noopener noreferrer"
            className="floating-btn youtube-btn"
            style={{ "--delay": "0.25s" }}
          >
            <FaYoutube />
            <span className="floating-tooltip">YouTube</span>
          </a>

          {/* WhatsApp */}
          <button
            ref={whatsappBtnRef}
            className="floating-btn whatsapp-btn"
            onClick={handleWhatsAppClick}
            aria-label="Contact via WhatsApp"
            style={{ "--delay": "0.3s" }}
          >
            <FaWhatsapp />
            <span className="floating-tooltip">Chat on WhatsApp</span>
          </button>

          {/* Call */}
          <button
            ref={callBtnRef}
            className="floating-btn call-btn"
            onClick={handleCallClick}
            aria-label="Call us"
            style={{ "--delay": "0.35s" }}
          >
            <FaPhone />
            <span className="floating-tooltip">Call us now</span>
          </button>
        </div>
      </div>

      <style jsx>{`
        /* ═══════════════════════════════════════════
           CONTAINER
           ═══════════════════════════════════════════ */
        .floating-contact-container {
          position: fixed;
          right: 20px;
          bottom: 30px;
          z-index: 1000;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 12px;
        }

        /* ═══════════════════════════════════════════
           TOGGLE MAIN BUTTON (السهم / X)
           ═══════════════════════════════════════════ */
        .toggle-btn {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #ae0001, #7a0001);
          color: #fff;
          font-size: 22px;
          box-shadow:
            0 8px 24px rgba(174, 0, 1, 0.45),
            inset 0 1px 0 rgba(255, 255, 255, 0.2);
          position: relative;
          z-index: 2;
        }

        .toggle-btn:hover {
          background: linear-gradient(135deg, #c40203, #900002);
          transform: translateY(-3px) scale(1.06);
          box-shadow:
            0 12px 32px rgba(174, 0, 1, 0.6),
            inset 0 1px 0 rgba(255, 255, 255, 0.25);
        }

        /* أيقونة السهم بتلف لما القايمة تفتح */
        .toggle-icon-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.35s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }

        .toggle-btn.open .toggle-icon-wrap {
          transform: rotate(360deg);
        }

        /* ═══════════════════════════════════════════
           MENU ITEMS CONTAINER
           ═══════════════════════════════════════════ */
        /* ═══════════════════════════════════════════
   MENU ITEMS CONTAINER
   ═══════════════════════════════════════════ */
        .menu-items {
          display: flex;
          flex-direction: column-reverse;
          align-items: flex-end;
          gap: 10px;

          /* مخفية كلياً — مافيش glass background */
          max-height: 0;
          overflow: hidden;
          opacity: 0;
          pointer-events: none;
          transform: translateY(10px) scale(0.6);
          transform-origin: bottom right;
          background: transparent;
          padding: 0;
          margin: 0;
          visibility: hidden;

          transition:
            max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
            opacity 0.25s ease,
            transform 0.35s cubic-bezier(0.68, -0.55, 0.265, 1.55),
            visibility 0s linear 0.4s,
            margin-bottom 0.3s ease;
        }

        .menu-items.visible {
          max-height: 500px; /* كبير كفاية عشان يظهر كل الأزرار */
          opacity: 1;
          pointer-events: auto;
          transform: translateY(0) scale(1);
          visibility: visible;
          margin-bottom: 4px;
          transition:
            max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
            opacity 0.3s ease,
            transform 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55),
            visibility 0s linear 0s,
            margin-bottom 0.3s ease;
        }

        /* ═══════════════════════════════════════════
           FLOATING BUTTONS (INSIDE MENU)
           ═══════════════════════════════════════════ */
        .floating-btn {
          width: 46px;
          height: 46px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          color: white;
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
          transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          position: relative;
          text-decoration: none !important;
        }

        /* Stagger animation عند فتح القائمة */
        .menu-items.visible .floating-btn {
          animation: menuItemPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)
            both;
          animation-delay: var(--delay, 0s);
        }

        @keyframes menuItemPop {
          0% {
            opacity: 0;
            transform: translateY(20px) scale(0);
          }
          70% {
            transform: translateY(-4px) scale(1.1);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .floating-btn:hover {
          transform: translateY(-4px) scale(1.1);
        }

        /* الأنواع المختلفة */
        .back-to-top-btn {
          background: linear-gradient(135deg, #ae0001, #7a0001);
        }
        .back-to-top-btn:hover {
          box-shadow: 0 10px 25px rgba(174, 0, 1, 0.5);
        }

        .whatsapp-btn {
          background: linear-gradient(135deg, #25d366, #128c7e);
        }
        .whatsapp-btn:hover {
          box-shadow: 0 10px 25px rgba(37, 211, 102, 0.5);
        }

        .call-btn {
          background: linear-gradient(135deg, #2c3b5b, #1a253a);
        }
        .call-btn:hover {
          box-shadow: 0 10px 25px rgba(44, 59, 91, 0.5);
        }

        .instagram-btn {
          background: linear-gradient(
            45deg,
            #f09433 0%,
            #e6683c 25%,
            #dc2743 50%,
            #cc2366 75%,
            #bc1888 100%
          );
        }
        .instagram-btn:hover {
          box-shadow: 0 10px 25px rgba(220, 39, 67, 0.5);
        }

        .facebook-btn {
          background: #1877f2;
        }
        .facebook-btn:hover {
          box-shadow: 0 10px 25px rgba(24, 119, 242, 0.5);
        }

        .tiktok-btn {
          background: #000000;
        }
        .tiktok-btn:hover {
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.6);
        }

        .youtube-btn {
          background: #ff0000;
        }
        .youtube-btn:hover {
          box-shadow: 0 10px 25px rgba(255, 0, 0, 0.5);
        }

        /* ═══════════════════════════════════════════
           PULSE (بس على زر التوجل الرئيسي)
           ═══════════════════════════════════════════ */
        .toggle-btn.pulse::before {
          content: "";
          position: absolute;
          inset: -6px;
          border-radius: 50%;
          border: 2px solid #ae0001;
          opacity: 0;
          animation: pulseRing 3s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
          pointer-events: none;
        }

        @keyframes pulseRing {
          0% {
            transform: scale(0.7);
            opacity: 0;
          }
          25% {
            opacity: 0.6;
          }
          50% {
            transform: scale(1.2);
            opacity: 0;
          }
          100% {
            transform: scale(1.4);
            opacity: 0;
          }
        }

        /* ═══════════════════════════════════════════
           TOOLTIP
           ═══════════════════════════════════════════ */
        .floating-tooltip {
          position: absolute;
          right: calc(100% + 14px);
          top: 50%;
          transform: translateY(-50%) translateX(6px);
          background: rgba(15, 23, 42, 0.95);
          color: white;
          padding: 8px 14px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.03em;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition:
            opacity 0.25s ease,
            transform 0.25s ease;
          z-index: 1001;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        .floating-tooltip::after {
          content: "";
          position: absolute;
          left: 100%;
          top: 50%;
          transform: translateY(-50%);
          border: 5px solid transparent;
          border-left-color: rgba(15, 23, 42, 0.95);
        }

        .floating-btn:hover .floating-tooltip {
          opacity: 1;
          transform: translateY(-50%) translateX(0);
        }

        /* ═══════════════════════════════════════════
           NOTIFICATION BADGE
           ═══════════════════════════════════════════ */
        .notification-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          background: #ff6b6b;
          color: white;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          font-size: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: bounce 2s ease infinite;
          box-shadow: 0 2px 8px rgba(255, 107, 107, 0.5);
          border: 2px solid #fff;
        }

        @keyframes bounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }

        /* ═══════════════════════════════════════════
           TOGGLE BUTTON ENTRANCE
           ═══════════════════════════════════════════ */
        .toggle-btn {
          opacity: 0;
          transform: translateY(60px) scale(0);
          transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }

        .toggle-btn.visible {
          opacity: 1;
          transform: translateY(0) scale(1);
        }

        /* ═══════════════════════════════════════════
           CLICK ANIMATION
           ═══════════════════════════════════════════ */
        .click-animation {
          animation: clickEffect 0.3s ease;
        }

        @keyframes clickEffect {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(0.85);
          }
          100% {
            transform: scale(1);
          }
        }

        /* ═══════════════════════════════════════════
           RESPONSIVE
           ═══════════════════════════════════════════ */
        @media (max-width: 768px) {
          .floating-contact-container {
            right: 14px;
            bottom: 20px;
            gap: 10px;
          }

          .toggle-btn {
            width: 50px;
            height: 50px;
            font-size: 18px;
          }

          .floating-btn {
            width: 42px;
            height: 42px;
            font-size: 16px;
          }

          .floating-tooltip {
            display: none !important;
          }

          .notification-badge {
            width: 18px;
            height: 18px;
            font-size: 8px;
          }
        }

        @media (max-width: 380px) {
          .toggle-btn {
            width: 46px;
            height: 46px;
            font-size: 16px;
          }

          .floating-btn {
            width: 38px;
            height: 38px;
            font-size: 14px;
          }
        }

        @media (min-width: 1440px) {
          .floating-contact-container {
            right: 30px;
            bottom: 40px;
          }

          .toggle-btn {
            width: 62px;
            height: 62px;
            font-size: 24px;
          }

          .floating-btn {
            width: 50px;
            height: 50px;
            font-size: 20px;
          }
        }
      `}</style>
    </>
  );
};
